import {
  findCase,
  findDocument,
  findIssue,
  officialDocuments,
  type OfficialCase,
  type OfficialDocument,
} from './scenario-center-content';
import { modalDismissProps, useModalFocus } from './scenario-modal-focus';

/**
 * The evidence drawer: 依据与案例.
 *
 * WHAT IT SHOWS
 *   For the issue the reader is on: why it is worth attention, the official documents
 *   cited with the article or report page each citation points at, the related incident
 *   cases with what the official material actually established, and a one-line note on
 *   what kind of source each one is.
 *
 * WHAT IT DOES NOT SHOW
 *   No JSON, no internal paths, no object codes, no version pins, no booleans. Those stay
 *   in the maintenance view. The drawer also never invents a case to fill a slot: the
 *   高处坠物 issue has no verified incident case and shows none.
 */
export function ScenarioEvidenceDrawer({
  issueCode,
  restoreFocusTo,
  onClose,
}: {
  readonly issueCode: string;
  readonly restoreFocusTo?: HTMLElement | null;
  readonly onClose: () => void;
}) {
  const dialog = useModalFocus(restoreFocusTo);
  const issue = findIssue(issueCode);
  const documents = (issue?.documents ?? [])
    .map((ref) => ({ ref, document: findDocument(ref.id) }))
    .filter(
      (row): row is { ref: (typeof row)['ref']; document: OfficialDocument } =>
        row.document !== undefined,
    );
  const cases = (issue?.cases ?? [])
    .map((ref) => ({ ref, incident: findCase(ref.id) }))
    .filter(
      (row): row is { ref: (typeof row)['ref']; incident: OfficialCase } =>
        row.incident !== undefined,
    );
  const historical = officialDocuments.filter(
    (document) =>
      document.kind === '历史专项文件' && documents.some((row) => row.document.id === document.id),
  );
  return (
    <dialog
      className="scc-drawer scc-evidence-drawer"
      ref={dialog}
      role="dialog"
      aria-modal="true"
      aria-labelledby="scc-evidence-title"
      {...modalDismissProps(onClose)}
    >
      <div className="scc-drawer-heading">
        <div>
          <p className="scc-drawer-eyebrow">高层建筑火灾</p>
          <h2 id="scc-evidence-title">依据与案例</h2>
        </div>
        <button autoFocus onClick={onClose} aria-label="关闭">
          关闭 ×
        </button>
      </div>
      <div className="scc-drawer-body">
        {!issue ? (
          <div className="scc-notice" role="status">
            该问题尚未建立依据记录。不展示替代内容。
          </div>
        ) : (
          <>
            <p className="scc-evidence-scene">高层建筑火灾</p>
            <h3 className="scc-evidence-issue">{issue.title}</h3>
            <p className="scc-evidence-why">{issue.explanation}</p>
            <h3>文件依据</h3>
            {documents.length === 0 ? (
              <div className="scc-notice" role="status">
                该问题尚未登记直接文件依据。
              </div>
            ) : (
              documents.map(({ ref, document }) => (
                <article className="scc-evidence-card" key={document.id + ref.locator}>
                  <span className="scc-chip">
                    {document.kind} · {document.publisher}
                  </span>
                  <h4>{document.name}</h4>
                  <p>{ref.relation}</p>
                  <p className="scc-evidence-locator">{ref.locator}</p>
                  <a href={document.url} target="_blank" rel="noopener noreferrer">
                    {document.linkLabel}
                    <span aria-hidden="true"> ↗</span>
                  </a>
                </article>
              ))
            )}
            <h3>相关案例</h3>
            {cases.length === 0 ? (
              <div className="scc-notice" role="status">
                本轮未取得经核验且适用于该问题的案例，因此不展示案例卡片。
              </div>
            ) : (
              cases.map(({ ref, incident }) => (
                <article className="scc-evidence-card" key={incident.id}>
                  <span className="scc-chip">
                    {incident.kind} · 发生于 {incident.date}
                  </span>
                  <h4>{incident.name}</h4>
                  <p>{incident.summary}</p>
                  <p>{ref.relation}</p>
                  {ref.locator && <p className="scc-evidence-locator">重点阅读：{ref.locator}</p>}
                  <p className="scc-evidence-classification">
                    资料性质：{incident.classification} · {incident.publisher}
                  </p>
                  <a href={incident.url} target="_blank" rel="noopener noreferrer">
                    {incident.linkLabel}
                    <span aria-hidden="true"> ↗</span>
                  </a>
                </article>
              ))
            )}
            {historical.length > 0 && (
              <>
                <h3>补充风险说明</h3>
                {historical.map((document) => (
                  <article className="scc-evidence-card" key={document.id}>
                    <span className="scc-chip">
                      {document.kind} · {document.publisher}
                    </span>
                    <h4>{document.name}</h4>
                    <p>{document.note}</p>
                    <a href={document.url} target="_blank" rel="noopener noreferrer">
                      {document.linkLabel}
                      <span aria-hidden="true"> ↗</span>
                    </a>
                  </article>
                ))}
              </>
            )}
            <div className="scc-notice">{issue.boundary}</div>
          </>
        )}
        <div className="scc-notice">
          文件依据与案例为官方公开资料；页面文字为平台依据这些资料整理，不代表官方原句，也不代表本地预案或行动授权。
          外部链接在新标签页打开，本页的当前问题不会改变。
        </div>
      </div>
    </dialog>
  );
}
